import { ApiUrl } from '@config/apiUrl';
import { useHireJob } from '@features/buyer/hire/hire-job-context';
import type { HireStepNavigation } from '@features/buyer/hire/hire-r8tr/hireR8trTypes';
import { PaymentStepView } from '@features/buyer/hire/payment-step/payment-step-view';
import { hostedFieldStyles } from '@features/payments/hostedFields';
import { useRemoveCardMutation } from '@features/payments/paymentApi';
import apiProtected from '@lib/api/apiClient';
import { apiErrorMessage } from '@lib/api/apiError';
import { fetchBuyerCost } from '@lib/api/costs';
import { useChargeCardMutation, useChargeSavedCardMutation } from '@lib/api/payments';
import { logError } from '@lib/logError';
import braintree, { type BraintreeHostedFields } from 'braintree-web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type {
  ApiEnvelope,
  DiscountResult,
  SavedCard,
} from './paymentStepTypes';


interface PaymentStepProps extends HireStepNavigation {
  handleTosterFire: (message: string) => void;
}

const readErrorMessage = (error: unknown, fallback: string): string => {
  return apiErrorMessage(error) || fallback;
};

const PaymentStep = ({
  handleTosterFire,
  onBack,
  onCancel,
}: PaymentStepProps): JSX.Element => {
  const { createJob, saveJob, resetJob } = useHireJob();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const chargeSavedCard = useChargeSavedCardMutation();
  const chargeCard = useChargeCardMutation();
  const removeCard = useRemoveCardMutation();
  const [clientToken, setClientToken] = useState<string | null>(null);
  const [discountData, setDiscountData] = useState<number | string | null>(
    null
  );
  const [discountCriteria, setDiscountCriteria] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [selectedCardToken, setSelectedCardToken] = useState<string | null>(
    null
  );
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const hostedFieldsRef = useRef<BraintreeHostedFields | null>(null);

  const fetchCards = useCallback(async () => {
    setCardsLoading(true);
    try {
      const res = await apiProtected.get<ApiEnvelope<SavedCard[]>>(
        `${ApiUrl.server_url}v1/braintree/get-all-payment-methods`
      );
      const cards = res?.data?.data ?? [];
      setSavedCards(cards);
      const defaultCard = cards.find((card) => card.default);
      if (defaultCard) {
        setSelectedCardToken(defaultCard.token);
      } else if (cards.length > 0) {
        setSelectedCardToken(cards[0].token);
      }
      setShowPaymentForm(cards.length === 0);
    } catch (error: unknown) {
      logError('fetch cards', error);
      setSavedCards([]);
      setShowPaymentForm(true);
    } finally {
      setCardsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiProtected.get<ApiEnvelope<string>>(
          `${ApiUrl.server_url}v1/braintree/get-client-token`
        );
        setClientToken(res.data.data ?? null);
      } catch (error: unknown) {
        logError('client token', error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!createJob?.buyerCost) return;
    apiProtected
      .get<ApiEnvelope<DiscountResult>>(
        `${ApiUrl.server_url}v1/discounts/calculate-discount-for-buyer?buyerCost=${createJob.buyerCost}`
      )
      .then((res) => {
        const finalBuyerCost = res?.data?.data?.finalBuyerCost;
        if (finalBuyerCost) {
          setDiscountData(finalBuyerCost);
          setDiscountCriteria(res.data.data?.discountCriteria ?? null);
        }
        saveJob({ discountedBuyerCost: finalBuyerCost || undefined });
      })
      .catch((error: unknown) => logError('payment fields', error));
  }, [createJob?.buyerCost, saveJob]);

  useEffect(() => {
    if (!clientToken || !showPaymentForm) return;

    let hostedFields: BraintreeHostedFields | undefined;
    const authorization = clientToken;
    braintree.client
      .create({ authorization })
      .then((clientInstance) =>
        braintree.hostedFields.create({
          client: clientInstance,
          styles: hostedFieldStyles(),
          fields: {
            number: {
              container: '#bt-card-number',
              placeholder: '•••• •••• •••• ••••',
            },
            expirationDate: {
              container: '#bt-expiry',
              placeholder: 'MM / YY',
            },
            cvv: { container: '#bt-cvv', placeholder: '•••' },
            cardholderName: {
              container: '#bt-name',
              placeholder: 'Name on card',
            },
          },
        })
      )
      .then((hostedFieldsInstance) => {
        hostedFields = hostedFieldsInstance;
        hostedFieldsRef.current = hostedFieldsInstance;

        hostedFieldsInstance.on('validityChange', () => {
          const state = hostedFieldsInstance.getState();
          const allValid = Object.values(state.fields).every(
            (field) => field.isValid
          );
          setIsReady(allValid);
        });
      })
      .catch((error: unknown) => logError('payment fields', error));

    return () => {
      hostedFields?.teardown();
      hostedFieldsRef.current = null;
    };
  }, [clientToken, showPaymentForm]);

  const buildJobData = () => ({
    categoryId: createJob.categoryId,
    subCategoryId: createJob.subCategoryId,
    askingPrice: createJob.askingPrice,
    buyerCost: createJob.buyerCost,
    description: createJob.description,
    vinOrSerial: createJob.vinOrSerial,
    year: createJob.year,
    make: createJob.make,
    model: createJob.model,
    link: createJob.listing,
    address: createJob.address,
    city: createJob.city,
    state: createJob.state,
    zip: createJob.zip,
    longitude: createJob.jobLongitude,
    latitude: createJob.jobLatitude,
    contactName: createJob.contactName,
    contactNumber: createJob.contactNumber,
    contactEmail: createJob.contactEmail,
    specialInstructions: createJob.specialInstructions,
    isDealer: createJob.contactType === 'DEALER',
    discountedBuyerCost: discountData || null,
  });

  const recalculateBuyerCost = useCallback(async () => {
    if (createJob?.categoryId == null || createJob?.askingPrice === '') {
      return false;
    }

    try {
      const nextBuyerCost = await fetchBuyerCost(
        createJob.categoryId,
        createJob.askingPrice
      );
      setDiscountData(null);
      setDiscountCriteria(null);
      saveJob({
        ...createJob,
        buyerCost: String(nextBuyerCost),
        discountedBuyerCost: undefined,
      });
      return true;
    } catch {
      return false;
    }
  }, [createJob, saveJob]);

  const handlePaymentError = async (error: unknown): Promise<void> => {
    logError('payment', error);
    const message = readErrorMessage(
      error,
      'Payment failed. Please try again.'
    );

    if (message === 'Invalid buyer cost for the provided asking price') {
      try {
        const updated = await recalculateBuyerCost();
        if (updated) {
          handleTosterFire(
            'Inspection fee was updated from the server. Please submit again.'
          );
          return;
        }
      } catch (recalcError: unknown) {
        logError('buyer cost recalculation', recalcError);
      }
    }

    handleTosterFire(message);
  };

  const handlePaymentSubmit = async (): Promise<void> => {
    if (savedCards.length > 0 && !showPaymentForm) {
      if (!selectedCardToken) {
        handleTosterFire('Please select a card');
        return;
      }
      setLoading(true);
      try {
        const amountToCharge = discountData || createJob.buyerCost;
        const response = await chargeSavedCard.mutateAsync({
          token: selectedCardToken,
          amount: amountToCharge,
          jobData: buildJobData(),
        });

        if (response?.status === 200) {
          toast.success(
            'Payment successful! Job created successfully.'
          );
          resetJob();
          setTimeout(() => {
            navigate('/buyer-dashboard');
          }, 2000);
        }
      } catch (error: unknown) {
        await handlePaymentError(error);
      } finally {
        setLoading(false);
      }
    } else {
      if (!hostedFieldsRef.current) return;

      setLoading(true);
      try {
        const { nonce } = await hostedFieldsRef.current.tokenize();
        const amountToCharge = discountData || createJob.buyerCost;

        const response = await chargeCard.mutateAsync({
          nonce,
          amount: amountToCharge,
          jobData: buildJobData(),
        });

        if (response?.status === 200) {
          toast.success(
            'Payment successful! Job created successfully.'
          );
          resetJob();
          setTimeout(() => {
            navigate('/buyer-dashboard');
          }, 2000);
        }
      } catch (error: unknown) {
        await handlePaymentError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteCard = async (token: string): Promise<void> => {
    try {
      await removeCard.mutateAsync(token);
      fetchCards();
    } catch (error: unknown) {
      logError('delete card', error);
    }
  };

  const displayCost = discountData || createJob?.buyerCost || '0.00';

  return (
    <PaymentStepView
      createJob={createJob}
      displayCost={displayCost}
      discountData={discountData}
      discountCriteria={discountCriteria}
      cardsLoading={cardsLoading}
      savedCards={savedCards}
      showPaymentForm={showPaymentForm}
      selectedCardToken={selectedCardToken}
      setSelectedCardToken={setSelectedCardToken}
      handleDeleteCard={handleDeleteCard}
      setShowPaymentForm={setShowPaymentForm}
      fetchCards={fetchCards}
      handlePaymentSubmit={handlePaymentSubmit}
      loading={loading}
      isReady={isReady}
      onBack={onBack}
      onCancel={onCancel}
    />
  );
};

export default PaymentStep;
